import { useCallback, useMemo } from 'react';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { createQueryOptions } from '../../../../query';
import { RequestMeta } from '../../../../temporary/requestmeta.types';
import { useStudentApi } from '../../../api';
import { studentKeys } from '../../../query-key-factory';
import { StudentFeesResponse, StudentFeesSearch } from './fees.types';

type GetStudentFeesListContext = {
  meta?: {
    studentId?: string;
    queryString?: string;
  };
};

const useGetStudentFeesList = () => {
  const StudentApi = useStudentApi();

  const getStudentFeesList = async (context: GetStudentFeesListContext) => {
    const { studentId, queryString } = context.meta || {};
    const { data } = await StudentApi.get<StudentFeesResponse>(
      `/consumer/students/${studentId}/fees?${queryString}`
    );
    return data;
  };

  return getStudentFeesList;
};

interface UseListStudentFeesArgs<T> {
  studentId: string;
  select?: (data: StudentFeesResponse) => T;
  queryOptions?: StudentFeesSearch;
}

/***
 * Gets all fees for a student by student id
 */
const useListConsumerStudentFees = <T = StudentFeesResponse>({
  studentId,
  select,
  queryOptions,
}: UseListStudentFeesArgs<T>) => {
  const getStudentFeesList = useGetStudentFeesList();
  const { queryString, queryObject } = createQueryOptions<StudentFeesSearch>({
    search: queryOptions,
  });

  const queryKey = useMemo(() => {
    if (!studentId) return [];
    return [...studentKeys.id('user').consumer.students.id(studentId).fees.all, queryObject];
  }, [studentId, queryObject]);

  return useQuery({
    queryKey,
    queryFn: getStudentFeesList,
    meta: { studentId, queryString },
    enabled: !!studentId,
    select,
  });
};
const useInfiniteListConsumerStudentFees = <T = StudentFeesResponse>({
  studentId,
  queryOptions,
}: Omit<UseListStudentFeesArgs<T>, 'pagination'>) => {
  const getStudentFeesList = useGetStudentFeesList();
  const { queryString: existingQueryString, queryObject } = createQueryOptions<StudentFeesSearch>({
    search: queryOptions,
  });
  const getInfiniteData = useCallback(
    async ({ pageParam = { limit: 25, offset: 0 } }) => {
      // Create a new query string with the existing query string and the new pagination
      const { queryString } = createQueryOptions({
        pagination: {
          limit: pageParam.limit,
          offset: pageParam.offset,
        },
        existingSearchString: existingQueryString,
      });
      const result = await getStudentFeesList({
        meta: { queryString, studentId },
      });
      return result;
    },
    [existingQueryString, getStudentFeesList, studentId]
  );

  const queryKey = studentKeys.id('user').consumer.students.id(studentId).fees.infinite;

  return useInfiniteQuery({
    queryKey: [...queryKey, queryObject],
    queryFn: getInfiniteData,
    enabled: !!studentId,
    initialPageParam: { limit: 24, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage as { meta: RequestMeta };
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

export { useInfiniteListConsumerStudentFees, useListConsumerStudentFees };
